import React, { useEffect, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AsyncSection, SegmentedControl } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { fetchAnalytics } from "./adTemplateApi";
import { int, money } from "./formatters";

const SERIES = {
  mode: [
    { key: "charged", label: "Paid", color: tokens.primary },
    { key: "free_tier", label: "Free tier", color: tokens.secondary },
    { key: "promotional", label: "Promo", color: tokens.link },
  ],
  format: [
    { key: "image", label: "Image", color: tokens.primary },
    { key: "video", label: "Video", color: tokens.info },
  ],
};

const SPLITS = [
  { value: "mode", label: "By billing mode" },
  { value: "format", label: "By format" },
];

const CHART_TITLE = {
  mode: "Ads generated by billing mode",
  format: "Ads generated by format",
};

const CURRENCIES = ["INR", "USD"];

const useStyles = makeStyles({
  sectionTitle: {
    fontFamily: tokens.fontBody,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: tokens.textHint,
    marginBottom: 10,
  },
  block: { marginTop: 26 },
  currency: {
    fontFamily: tokens.fontHeading,
    fontWeight: 700,
    fontSize: 13,
    color: tokens.primary,
    marginBottom: 6,
  },
  flush: { marginBottom: 0 },
  chartHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "9px 0",
    borderBottom: `1px solid ${tokens.divider}`,
    fontFamily: tokens.fontBody,
    fontSize: 13,
    color: tokens.textPrimary,
  },
  amount: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  subRow: { paddingLeft: 16, color: tokens.textSecondary, fontSize: 12.5 },
  subAmount: { fontWeight: 600, fontVariantNumeric: "tabular-nums" },
  count: { color: tokens.textHint, fontSize: 12, marginLeft: 8 },
  note: {
    fontFamily: tokens.fontBody,
    fontSize: 11.5,
    color: tokens.textHint,
    marginTop: 10,
  },
});

const SplitRow = ({ classes, label, value, count, code, testid }) => (
  <Box className={`${classes.row} ${classes.subRow}`} data-testid={testid}>
    <span>{label}</span>
    <span>
      <span className={classes.subAmount}>{money(value, code)}</span>
      <span className={classes.count}>{int(count)} ads</span>
    </span>
  </Box>
);

export default function RevenueView({ range, refreshKey, format = "" }) {
  const classes = useStyles();
  const [split, setSplit] = useState("mode");
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    data: null,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("revenue", {
      from: range.from,
      to: range.to,
      ...(format ? { format } : {}),
    }).then((r) => {
      if (!alive) return;
      setState({ loading: false, notFound: r.notFound, data: r.data, error: r.error });
    });
    return () => {
      alive = false;
    };
  }, [range.from, range.to, format, refreshKey]);

  const series = (state.data && state.data.series) || [];
  const summary = (state.data && state.data.summary) || {};

  return (
    <AsyncSection
      loading={state.loading}
      notFound={state.notFound}
      error={state.error}
      empty={!series.length}
      emptyTitle="No ad templates in this range"
      emptyDescription="Nothing was generated between these dates."
    >
      <Box data-testid="revenue-body">
        <Box className={classes.chartHead}>
          <Typography component="div" className={`${classes.sectionTitle} ${classes.flush}`}>
            {CHART_TITLE[split]}
          </Typography>
          <SegmentedControl
            value={split}
            onChange={setSplit}
            options={SPLITS}
            data-testid="revenue-split"
          />
        </Box>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={tokens.divider} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: tokens.textHint }}
              tickLine={false}
              axisLine={{ stroke: tokens.border }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: tokens.textHint }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {SERIES[split].map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stackId="1"
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.22}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>

        {CURRENCIES.map((code) => {
          const bucket = summary[code];
          if (!bucket) return null;
          const givenAds = (bucket.freeAds || 0) + (bucket.promoAds || 0);
          const givenVideoAds = (bucket.freeVideoAds || 0) + (bucket.promoVideoAds || 0);
          const showSplit = !format;
          return (
            <Box key={code} className={classes.block} data-testid={`summary-${code}`}>
              <Typography component="div" className={classes.currency}>{code}</Typography>
              <Box className={classes.row}>
                <span>Revenue billed</span>
                <span>
                  <span className={classes.amount}>{money(bucket.billed, code)}</span>
                  <span className={classes.count}>{int(bucket.chargedAds)} ads</span>
                </span>
              </Box>
              {showSplit && (
                <>
                  <SplitRow
                    classes={classes}
                    label="Image"
                    value={bucket.billedImage}
                    count={(bucket.chargedAds || 0) - (bucket.chargedVideoAds || 0)}
                    code={code}
                    testid={`billed-image-${code}`}
                  />
                  <SplitRow
                    classes={classes}
                    label="Video"
                    value={bucket.billedVideo}
                    count={bucket.chargedVideoAds}
                    code={code}
                    testid={`billed-video-${code}`}
                  />
                </>
              )}
              <Box className={classes.row}>
                <span>Free tier + promo given</span>
                <span>
                  <span className={classes.amount}>{money(bucket.freeValue, code)}</span>
                  <span className={classes.count}>{int(givenAds)} ads</span>
                </span>
              </Box>
              {showSplit && (
                <>
                  <SplitRow
                    classes={classes}
                    label="Image"
                    value={bucket.freeValueImage}
                    count={givenAds - givenVideoAds}
                    code={code}
                    testid={`free-image-${code}`}
                  />
                  <SplitRow
                    classes={classes}
                    label="Video"
                    value={bucket.freeValueVideo}
                    count={givenVideoAds}
                    code={code}
                    testid={`free-video-${code}`}
                  />
                </>
              )}
            </Box>
          );
        })}

        <Typography component="div" className={classes.note}>
          INR and USD are reported separately and never added together.
        </Typography>
      </Box>
    </AsyncSection>
  );
}
